import type { InternalAxiosRequestConfig } from 'axios';

import { isAuthSessionExpiredError } from '@shared/errors/auth-session-expired-error';
import { acquireAccessToken } from '@shared/helpers/acquire-access-token/acquireAccessToken';
import { handleSessionExpired } from '@shared/helpers/session-expiry/handleSessionExpired';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { setForbidden } from '../forbidden-store/forbiddenStore';

interface RequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export function responseSuccessInterceptor(
  response: AxiosResponse
): AxiosResponse {
  return response;
}

/**
 * Response error interceptor:
 * - 401: Forces one token refresh and retries. If the session is expired, shows the session-expired screen.
 * - 403: Sets global forbidden state (renders Forbidden page inline, no redirect).
 * - All others: Re-thrown for React Query / callers to handle.
 */
export async function responseErrorInterceptor(
  error: AxiosError
): Promise<AxiosResponse> {
  const originalRequest = error.config as RequestConfigWithRetry;

  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    try {
      const newToken = await acquireAccessToken({ forceRefresh: true });

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      if (isAuthSessionExpiredError(refreshError)) {
        void handleSessionExpired();
      }
      throw refreshError;
    }
  }

  if (error.response?.status === 403) {
    setForbidden();
    throw error;
  }

  throw error;
}
